import {
  FormCircuit,
  FormGlazedSurface,
  FormProcessOutputType,
  FormPropertyType,
  HpStep,
  InstallationType,
  ProjectPropertyKeyType,
  ProjectPropertyType
} from '@/helper/Types'
import formStore from '@/store/FormStore'
import projectStore from '@/store/ProjectStore'
import { generateRandomId } from '@/helper/tools'
import { ProcessFunctions } from '@/helper/ProcessFunctions'
import appStore from '@/store/AppStore'
import ProjectController from '@/controllers/ProjectController'

export default class FormController {
  /**
   * Gets called by the Steps controller to load the data in the fromStore
   */
  public static loadFormData(step: HpStep = appStore.currentStep) {
    formStore.data = {}

    const stepPropKeys: (keyof ProjectPropertyType)[] = Object.keys(projectStore.projectProperties).filter(
      propKey => projectStore.projectProperties[propKey as keyof ProjectPropertyType].step === step
    ) as (keyof ProjectPropertyType)[]

    stepPropKeys.forEach(key => {
      formStore.data[key] = ProcessFunctions.getProcessFunction(projectStore.projectProperties[key].formProcessType)(
        projectStore.projectProperties[key].val
      )
    })

    FormController.captureInitialData()
  }

  public static captureInitialData() {
    formStore.initialData = JSON.parse(JSON.stringify(formStore.data))
  }

  /**
   * Generates an empty circuit depending on the installation type ProjectStore
   * --> Independent on state in FormStore
   */
  public static getEmptyCircuit(): FormCircuit {
    return {
      id: generateRandomId(),
      cooling: projectStore.isCooling && !projectStore.isHeating
    } as FormCircuit
  }

  /**
   * Generates an empty circuit depending on the installation type in FormStore
   * --> Dependent on state in FormStore
   */
  public static getEmptyFormCircuit(): FormCircuit {
    return {
      id: generateRandomId(),
      cooling: FormController.isCooling && !FormController.isHeating
    } as FormCircuit
  }

  public static get isHeating(): boolean {
    return (formStore.data.installationTypes as string[]).includes(InstallationType.HEATING)
  }

  public static get isCooling(): boolean {
    return (formStore.data.installationTypes as string[]).includes(InstallationType.COOLING)
  }

  public static getEmptyGlacedSurface(): FormGlazedSurface {
    return {
      id: generateRandomId(),
      name: '',
      orientation: '',
      width: '',
      height: '',
      shaded: false
    } as FormGlazedSurface
  }

  /**
   * Reverts form data into projectStore.projectProperties val,
   * extracts the exact step to reset from by checking the property reset mapping
   * and triggers the reset
   */
  public static saveFormData() {
    let resetStep!: HpStep

    Object.keys(formStore.data).forEach(propertyName => {
      const prop = projectStore.projectProperties[propertyName as ProjectPropertyKeyType]
      const formVal = formStore.data[propertyName as keyof Partial<FormPropertyType>] as FormProcessOutputType
      const revertedVal = ProcessFunctions.getRevertFunction(prop.formRevertType)(formVal)

      if (JSON.stringify(revertedVal) === JSON.stringify(prop.val)) {
        return
      }

      prop.val = revertedVal

      if (prop.resetsFromStep === HpStep.EMPTY) {
        return
      }
      resetStep = resetStep ? appStore.getLowerStep(resetStep, prop.resetsFromStep) : prop.resetsFromStep
    })

    if (resetStep) {
      ProjectController.resetFromStep(resetStep)
    }
  }

  /**
   * Checks if the current step has changes made by the user in the form
   * Compares the form data with and initially saved state
   * @return true if changes detected
   */
  public static hasUnsavedChanges(): boolean {
    return !FormController.objectsAreEqual(formStore.data, formStore.initialData)
  }

  public static objectsAreEqual(obj1: unknown, obj2: unknown): boolean {
    if (typeof obj1 !== typeof obj2) {
      return false
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) {
        return false
      }
      return !obj1.some((element, index) => !FormController.objectsAreEqual(obj1[index], obj2[index]))
    }

    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
      const o1 = obj1 as Record<string, unknown>
      const o2 = obj2 as Record<string, unknown>

      // Check if one of the objects is not equal and then return false (double negation!)
      return !Object.keys({ ...o1, ...o2 }).some(key => {
        if ((o1[key] === undefined && !Object.keys(o2).includes(key) || (o2[key] === undefined && !Object.keys(o1).includes(key)))) {
          return false
        }

        if (!Object.keys(o1).includes(key) || !Object.keys(o2).includes(key)) {
          return true
        }

        if ((o1[key] === undefined && o2[key] !== undefined) || (o2[key] === undefined && o1[key] !== undefined)) {
          return true
        }

        return !FormController.objectsAreEqual(o1[key], o2[key])
      })
    }

    return obj1 === obj2
  }
}
